/**
 * Pure helpers for entity form field debug/tooltips.
 * Used by GenericEntityUpsertDialog and any consumer that needs field label tooltips.
 */

import type { EntityEntitlementForSchemaResponse, EntityEntitlementForTargetResponse } from '../../types/EntityEntitlements';
import {
  formatTargetEntitlementItemForDisplay,
  formatTier2AttributeForDisplay,
  getTargetEntitlementItemForAttribute,
  getTargetEntitlementsList,
  getTier2AttributeBindingForAttribute,
} from '../../types/EntityEntitlements';

/** Minimal attribute shape for tooltip (type, description, Length from tier3). */
export interface FieldTooltipAttribute {
  type?: string;
  description?: string;
  Description?: string;
  Length?: number;
  [key: string]: unknown;
}

/**
 * Build tooltip title for a form field label (data type, optional Length, optional description).
 */
export function getFieldTooltipTitle(attribute: FieldTooltipAttribute | null | undefined): string {
  const typeLabel = (attribute?.type || 'string').toString();
  const parts: string[] = [`Type: ${typeLabel}`];
  if (attribute?.Length != null) parts.push(`Length: ${attribute.Length}`);
  const desc = attribute?.description ?? attribute?.Description;
  if (desc && String(desc).trim()) parts.push(String(desc).trim());
  return parts.join('. ');
}

export interface ReferenceFetchStatusEntry {
  status: string;
  error?: string;
}

/** Inputs for building per-field debug tooltip lines. */
export interface FieldDebugTooltipContext {
  rawEntitySnapshot: Record<string, unknown> | null;
  tier2Entitlement: EntityEntitlementForSchemaResponse | null;
  tier2EntitlementError: string | null;
  targetEntitlement: EntityEntitlementForTargetResponse | null;
  tier3EntitlementError: string | null;
  isEditMode: boolean;
  tier3Settled: boolean;
  referenceFetchStatus: Record<string, ReferenceFetchStatusEntry>;
}

/**
 * Build debug tooltip lines for a field (renderer + raw value + fetch status).
 */
export function buildFieldDebugTooltipLines(
  fieldName: string,
  ctx: FieldDebugTooltipContext,
  rendererKey?: string,
  _isStubValue?: boolean
): string[] {
  const lines: string[] = [];
  lines.push(`Renderer: ${rendererKey ?? '—'}`);
  lines.push('');
  const raw = ctx.rawEntitySnapshot != null ? ctx.rawEntitySnapshot[fieldName] : undefined;
  if (raw === undefined) {
    lines.push('Raw value: (not in fetch)');
  } else if (raw === null) {
    lines.push('Raw value: null');
  } else if (typeof raw === 'string') {
    const preview = raw.length > 80 ? raw.slice(0, 80) + '…' : raw;
    lines.push(`Raw value: string, length ${raw.length}`);
    lines.push(preview.replace(/\n/g, '↵'));
  } else if (Array.isArray(raw)) {
    lines.push(`Raw value: array, length ${raw.length}`);
    lines.push(JSON.stringify(raw).slice(0, 100) + (JSON.stringify(raw).length > 100 ? '…' : ''));
  } else if (typeof raw === 'object') {
    const keys = Object.keys(raw).slice(0, 8).join(', ');
    lines.push(`Raw value: object, keys: ${keys}${Object.keys(raw).length > 8 ? '…' : ''}`);
    const preview = JSON.stringify(raw).slice(0, 80) + (JSON.stringify(raw).length > 80 ? '…' : '');
    lines.push(preview);
  } else {
    lines.push(`Raw value: ${typeof raw} = ${String(raw)}`);
  }
  lines.push('');
  lines.push('Fetch status:');
  lines.push(
    `Tier2 (schema/selector): ${ctx.tier2Entitlement != null ? '✓ Loaded' : ctx.tier2EntitlementError ? `✗ ${ctx.tier2EntitlementError.slice(0, 50)}${ctx.tier2EntitlementError.length > 50 ? '…' : ''}` : '—'}`
  );
  if (ctx.isEditMode && ctx.targetEntitlement == null) {
    lines.push(
      `Tier3 (edit entitlement): ${ctx.tier3EntitlementError ? `✗ ${ctx.tier3EntitlementError.slice(0, 50)}${ctx.tier3EntitlementError.length > 50 ? '…' : ''}` : ctx.tier3Settled ? '—' : '…'}`
    );
  }
  const refStatus = ctx.referenceFetchStatus[fieldName];
  if (refStatus) {
    const s =
      refStatus.status === 'success'
        ? '✓'
        : refStatus.status === 'error'
          ? `✗ ${(refStatus.error || '').slice(0, 40)}`
          : refStatus.status === 'loading'
            ? '…'
            : '—';
    lines.push(`Reference resolve: ${s}`);
  }
  const tier3Item = getTargetEntitlementItemForAttribute(ctx.targetEntitlement ?? null, fieldName);
  if (tier3Item) {
    lines.push('');
    lines.push('Attribute entitlement (Tier3):');
    lines.push(formatTargetEntitlementItemForDisplay(tier3Item));
  } else if (ctx.targetEntitlement != null && getTargetEntitlementsList(ctx.targetEntitlement).length > 0) {
    lines.push('');
    lines.push('Attribute entitlement (Tier3): (not in tier3 list)');
  }
  if (ctx.tier2Entitlement != null) {
    const tier2Item = getTier2AttributeBindingForAttribute(ctx.tier2Entitlement, fieldName);
    if (tier2Item) {
      lines.push('');
      lines.push('Attribute entitlement (Tier2):');
      lines.push(formatTier2AttributeForDisplay(tier2Item.attribute, tier2Item.binding));
    } else {
      lines.push('');
      lines.push('Attribute entitlement (Tier2): (not in tier2 list)');
    }
  }
  return lines;
}
